import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { PlusIcon } from '@phosphor-icons/react';
import type { CashReceiptDetail } from '@erp/rpc';
import { useCreateCashReceiptLogTask } from '@/api/rpc/hooks/accounts-receivable/useArCashReceipts';
import { useToast } from 'src/context/ToastContext';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { Textarea } from 'src/components/ui/textarea';
import {
  TaskTypeAgentPicker,
  TaskTypeUserPicker,
} from 'src/pages/authenticated/settings/organization/components/cards/task-types/TaskTypeDefaultAssigneeField';

type Priority = 'urgent' | 'high' | 'medium' | 'low';
type AssigneeMode = 'default' | 'user' | 'agent';

const PRIORITIES: Priority[] = ['urgent', 'high', 'medium', 'low'];

/**
 * The title the backend's automatic filing path builds, mirrored here so the
 * field opens pre-filled with what an unedited submit would produce.
 */
function defaultTitle(receipt: CashReceiptDetail): string {
  const amount = receipt.paymentAmount ? ` — $${receipt.paymentAmount}` : '';
  const from = receipt.customerName ?? receipt.bankDescription;
  return `Log cash receipt${amount}${from ? ` from ${from}` : ''}`;
}

interface CreateLogTaskDialogProps {
  receipt: CashReceiptDetail;
}

/**
 * Hand-file the receipt's log-cash-receipt task.
 *
 * The ingest hook files these automatically, but it skips Famous-origin
 * receipts, cannot be re-run for a receipt that predates the queue, and
 * chooses the title, priority and assignee itself. This is the deliberate
 * path: the operator names the work and picks who does it — including
 * pointing it at a keying agent, which is the only way to hand one a specific
 * receipt.
 */
export function CreateLogTaskDialog({ receipt }: CreateLogTaskDialogProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const createTask = useCreateCashReceiptLogTask();

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [dueDate, setDueDate] = useState('');
  const [assigneeMode, setAssigneeMode] = useState<AssigneeMode>('default');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [assigneeAgentDefinitionId, setAssigneeAgentDefinitionId] =
    useState('');

  const priorityLabels: Record<Priority, string> = {
    urgent: gt('Urgent', { context: 'task priority' }),
    high: gt('High', { context: 'task priority' }),
    medium: gt('Medium', { context: 'task priority' }),
    low: gt('Low', { context: 'task priority' }),
  };

  // An assignee mode with no selection would silently file the task
  // unassigned, which reads as a bug when the operator picked "agent".
  const assigneeIncomplete =
    (assigneeMode === 'user' && !assigneeUserId) ||
    (assigneeMode === 'agent' && !assigneeAgentDefinitionId);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) {
      setTitle(defaultTitle(receipt));
      setDescription('');
      setPriority('medium');
      setDueDate('');
      setAssigneeMode('default');
      setAssigneeUserId('');
      setAssigneeAgentDefinitionId('');
    }
  }

  async function handleSubmit() {
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    try {
      const result = await createTask.mutateAsync({
        receiptId: receipt.id,
        title: trimmedTitle.length > 0 ? trimmedTitle : undefined,
        description:
          trimmedDescription.length > 0 ? trimmedDescription : undefined,
        priority,
        dueDate: dueDate.length > 0 ? new Date(dueDate) : undefined,
        ...(assigneeMode === 'user' && {
          assigneeType: 'user' as const,
          assigneeUserId,
        }),
        ...(assigneeMode === 'agent' && {
          assigneeType: 'agent' as const,
          assigneeAgentDefinitionId,
        }),
      });
      showToast({
        title: result.created
          ? gt('Task created', {
              context: 'toast title, cash receipt logging task filed',
            })
          : gt('Task already open', {
              context:
                'toast title, the cash receipt already had an open logging task',
            }),
        message: result.created
          ? ''
          : gt('This receipt already had an open logging task.', {
              context:
                'toast message, no duplicate task was created for the receipt',
            }),
        color: result.created ? 'success' : 'warning',
        icon: result.created ? 'check' : 'warning',
      });
      setOpen(false);
    } catch (error) {
      showToast({
        title: gt('Could not create task', {
          context: 'toast title, filing a cash receipt logging task failed',
        }),
        message: error instanceof Error ? error.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-6 px-2 text-xs"
        onClick={() => handleOpenChange(true)}
      >
        <PlusIcon className="h-4 w-4" />
        <T context="button, file a logging task for this cash receipt">
          Create Task
        </T>
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <T context="dialog title, file a logging task for a cash receipt">
                Create Logging Task
              </T>
            </DialogTitle>
            <DialogDescription>
              <T context="dialog description, explains what the cash receipt logging task tracks">
                Tracks keying this receipt into the ledger of record. The
                assignee reads the receipt, its remittance and its invoice
                applications from the receipt itself, so nothing here needs to
                repeat them.
              </T>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="log-task-title">
                <T context="field label, task title">Title</T>
              </Label>
              <Input
                id="log-task-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="log-task-description">
                <T context="field label, task description">Description</T>
              </Label>
              <Textarea
                id="log-task-description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={gt('Anything the keyer should know…', {
                  context: 'placeholder for the logging task description',
                })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="log-task-priority">
                  <T context="field label, task priority">Priority</T>
                </Label>
                <Select
                  value={priority}
                  onValueChange={(value) => setPriority(value as Priority)}
                >
                  <SelectTrigger id="log-task-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {priorityLabels[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="log-task-due-date">
                  <T context="field label, task due date">Due Date</T>
                </Label>
                <Input
                  id="log-task-due-date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                <T context="field label, who the task is assigned to">
                  Assign To
                </T>
              </Label>
              <RadioGroup
                value={assigneeMode}
                onValueChange={(value) =>
                  setAssigneeMode(value as AssigneeMode)
                }
                className="flex items-center gap-4"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="default" id="log-task-default" />
                  <Label htmlFor="log-task-default" className="font-normal">
                    <T context="assignee option, use the task type's default assignee">
                      Task type default
                    </T>
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="user" id="log-task-user" />
                  <Label htmlFor="log-task-user" className="font-normal">
                    <T context="assignee option, a person">User</T>
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="agent" id="log-task-agent" />
                  <Label htmlFor="log-task-agent" className="font-normal">
                    <T context="assignee option, an AI agent">Agent</T>
                  </Label>
                </div>
              </RadioGroup>

              {assigneeMode === 'user' && (
                <TaskTypeUserPicker
                  value={assigneeUserId}
                  onChange={setAssigneeUserId}
                />
              )}
              {assigneeMode === 'agent' && (
                <TaskTypeAgentPicker
                  value={assigneeAgentDefinitionId}
                  onChange={setAssigneeAgentDefinitionId}
                />
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createTask.isPending}
            >
              <T context="button, dismiss the create logging task dialog">
                Cancel
              </T>
            </Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={createTask.isPending || assigneeIncomplete}
            >
              <T context="button, confirm creating the logging task">
                Create Task
              </T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
